import { useEffect, useState, useCallback } from 'react';
import { Badge, Alert } from '@dreamtree-org/twreact-ui';
import { useStableApi } from '@/lib/api';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Browser Commands" (`/app-browser-command`) — the audit log of every command sent to a
 * paired browser, across all of them.
 *
 * Read-only by construction: this is the record of what an agent asked a real signed-in
 * browser to do, so nothing here is editable. The rows come from
 * GET /api/browser-devices/commands (routes/browserDevicesRouter.js), which joins the device
 * name in — a log that cannot say which browser acted is not much of a log.
 *
 * Gated on `browser.view.org` like the device list. That permission's own description already
 * covers "the log of commands they were sent", so the page adds no new permission and needs no
 * `rbac:setup` re-run.
 */

/** A failed command is the interesting row, so status is loud rather than neutral text. */
function StatusBadge({ status }) {
  if (status === 'failed' || status === 'error') return <Badge variant="error" size="sm">{status}</Badge>;
  if (status === 'done' || status === 'completed') return <Badge variant="success" size="sm">{status}</Badge>;
  if (status === 'expired') return <Badge variant="warning" size="sm">{status}</Badge>;
  return <Badge variant="secondary" size="sm">{status || '—'}</Badge>;
}

const formatWhen = (value) => (value ? new Date(value).toLocaleString() : '—');

/** Static — rebuilt per render otherwise, and DataTable re-renders a cell on every change. */
const COMMAND_COLUMNS = [
  { key: 'device_name', label: 'Browser', render: (row) => row.device_name || `device #${row.device_id}` },
  { key: 'verb', label: 'Command', render: (row) => <code className="text-xs">{row.verb}</code> },
  { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
  {
    key: 'origin',
    label: 'Site',
    render: (row) => (row.origin
      ? <span className="text-xs">{row.origin}</span>
      : <span className="text-xs text-[var(--app-muted)]">—</span>),
  },
  {
    key: 'error',
    label: 'Error',
    // The message, not the whole stack — the row is a summary; the detail belongs in a
    // fuller view if one is ever needed.
    render: (row) => (row.error
      ? <span className="text-xs text-[var(--app-danger,inherit)]">{String(row.error).slice(0, 120)}</span>
      : <span className="text-xs text-[var(--app-muted)]">—</span>),
  },
  { key: 'issued_at', label: 'Sent', render: (row) => formatWhen(row.issued_at) },
  { key: 'completed_at', label: 'Finished', render: (row) => formatWhen(row.completed_at) },
];

export default function BrowserCommandsPage() {
  // useStableApi, never useAppApi: the latter returns a new object every render, and putting
  // it in a deps array makes this page refetch forever (the loop that took out
  // BrowserDevicesPage, and what client/src/lib/apiDepConvention.test.js now guards).
  const api = useStableApi();
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Empty deps are load-bearing — `api` is stable, so `load` is built once and the effect
  // below runs once on mount.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.fetchApi('browserCommandLog');
      setCommands(res.commands || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell>
      <PageHeader
        title="Browser Commands"
        subtitle="What agents asked paired browsers to do — the audit trail behind every browser tool call."
      />

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <Alert variant="info" className="mb-4" title="Read-only">
        Every command an agent sent to a paired browser is recorded here, newest first — including
        the ones a person declined and the ones that failed. The browser itself is not driven from
        this screen.
      </Alert>

      <DataTable
        data={commands}
        columns={COMMAND_COLUMNS}
        loading={loading}
        search
        emptyText="No browser commands yet. Commands appear here once an agent drives a paired browser."
        onReload={load}
      />
    </PageShell>
  );
}
